import React, { Fragment } from 'react';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { TextFieldType } from '../../components/TextField/TextField';

export type ConfirmationOptions = {
  title?: string;
  description?: string;
  catchOnCancel?: boolean;
  onSubmit?: (text?: string) => void;
  onCancel?: () => void;
  confirmText?: string;
  blockOutsideClick?: boolean;
  cancelText?: string;
  inputBox?: boolean;
  inputType?: TextFieldType;
  inputBoxIntialValue?: string;
  placeholderTx?: string;
  confirmStyle?: 'standard' | 'green' | 'red' | 'fit-big-text';
  descriptionStyle?: 'fit-big-text';
  isConfirmDisabledWhenEmpty?: boolean;
};

const ConfirmationServiceContext = React.createContext<
  // we will pass the openning dialog function directly to consumers
  (options: ConfirmationOptions) => Promise<void>
>(Promise.reject);

export const useConfirmation = () => React.useContext(ConfirmationServiceContext);

let imperativeConfirm: ((options: ConfirmationOptions) => Promise<void>) | null = null;

export function showConfirmDialog(options: ConfirmationOptions): Promise<void> {
  if (!imperativeConfirm) return Promise.resolve();
  return imperativeConfirm(options);
}

export const ConfirmationServiceProvider: React.FunctionComponent = ({ children }) => {
  const [confirmationState, setConfirmationState] = React.useState<ConfirmationOptions | null>(
    null
  );
  // const [open, setOpen] = React.useState<boolean>(false);
  //const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const awaitingPromiseRef = React.useRef<{
    resolve: () => void;
    reject: () => void;
  }>();

  const openConfirmation = (options: ConfirmationOptions) => {
    setConfirmationState(options);
    return new Promise<void>((resolve, reject) => {
      // save the promise result to the ref
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  React.useEffect(() => {
    imperativeConfirm = openConfirmation;
    return () => {
      imperativeConfirm = null;
    };
  }, []);

  const handleClose = () => {
    // Mostly always you don't need to handle canceling of alert dialog
    // So shutting up the unhandledPromiseRejection errors
    // if (confirmationState?.catchOnCancel && awaitingPromiseRef.current) {
    //   awaitingPromiseRef.current.reject();
    // }
    confirmationState?.onCancel?.();
    setConfirmationState(null);
  };

  const dismiss = async () => setConfirmationState(null);

  const handleSubmit = async (text?: string) => {
    confirmationState?.onSubmit?.(text);
    setConfirmationState(null);
  };
  return (
    <Fragment>
      {/* eslint-disable-next-line react/no-children-prop */}
      <ConfirmationServiceContext.Provider value={openConfirmation} children={children} />
      {confirmationState && (
        <ConfirmDialog
          onSubmit={confirmationState?.onSubmit && handleSubmit}
          onCancel={confirmationState?.onCancel && handleClose}
          dismiss={dismiss}
          title={confirmationState?.title ?? ''}
          description={confirmationState?.description}
          isLoading={false}
          confirmText={confirmationState?.confirmText}
          cancelText={confirmationState?.cancelText}
          inputBox={confirmationState?.inputBox}
          inputType={confirmationState?.inputType ?? TextFieldType.TEXT}
          initialInputValue={confirmationState?.inputBoxIntialValue}
          placeholderTx={confirmationState?.placeholderTx}
          confirmStyle={confirmationState?.confirmStyle}
          descriptionStyle={confirmationState?.descriptionStyle}
          blockOutsideClick={confirmationState?.blockOutsideClick}
          isConfirmDisabledWhenEmpty={confirmationState?.isConfirmDisabledWhenEmpty}
          // {...confirmationState}
        />
      )}
    </Fragment>
  );
};
